import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Tag, Tooltip, Modal, Form,
  Input, message, Space, Popconfirm, Tabs, InputNumber
} from 'antd';
import { CarryOutOutlined, ReloadOutlined, CheckOutlined, CloseOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchEmployees, fetchLeaves, fetchLeaveBalances, createLeaveBalance, adjustLeaveBalance, deleteLeaveBalance, cancelLeave, leaveManagerAction, leaveHrAction } from '../api';
import { LEAVE_TYPE_OPTIONS, LEAVE_STATUS_OPTIONS, tagColor, tagLabel } from '../constants/hrConstants';

const { Title, Text } = Typography;

export default function LeaveManagement() {
  const { can } = usePermissions();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: null, leaveType: null });
  const [activeTab, setActiveTab] = useState('applications');

  // Balances Tab State
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [adjustModal, setAdjustModal] = useState({ open: false, row: null });
  const [newAllocated, setNewAllocated] = useState(0);
  
  const [addBalanceModal, setAddBalanceModal] = useState(false);
  const [addBalanceForm] = Form.useForm();
  const [addingBalance, setAddingBalance] = useState(false);

  const [reasonModal, setReasonModal] = useState({ open: false, row: null, kind: null });
  const [reason, setReason] = useState('');

  const isManagerOrHr = can('hrLeave', 'approve');
  const canEditLeave = can('hrLeave', 'edit');

  useEffect(() => { fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {}); }, []);

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filters.status) params.status = filters.status;
      if (filters.leaveType) params.leaveType = filters.leaveType;
      const res = await fetchLeaves(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load leaves'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (activeTab === 'applications') loadLeaves(); }, [loadLeaves, activeTab]);

  const loadBalances = useCallback(async () => {
    if (!selectedEmployee) {
      setBalances([]);
      return;
    }
    setBalancesLoading(true);
    try {
      const res = await fetchLeaveBalances({ employeeId: selectedEmployee });
      setBalances(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load balances'); }
    finally { setBalancesLoading(false); }
  }, [selectedEmployee]);

  useEffect(() => { if (activeTab === 'balances') loadBalances(); }, [loadBalances, activeTab]);

  const act = async (fn, ...args) => { try { await fn(...args); message.success('Done'); loadLeaves(); } catch (e) { message.error(e?.response?.data?.message || 'Action failed'); } };

  const submitReason = async () => {
    const { row, kind } = reasonModal;
    try {
      if (kind === 'managerReject') await leaveManagerAction(row._id, { approve: false, comment: reason });
      else await leaveHrAction(row._id, { approve: false, comment: reason });
      message.success('Rejected'); setReasonModal({ open: false, row: null, kind: null }); setReason(''); loadLeaves();
    } catch (e) { message.error(e?.response?.data?.message || 'Failed'); }
  };

  const submitAdjustBalance = async () => {
    try {
      await adjustLeaveBalance(adjustModal.row._id, newAllocated);
      message.success('Balance adjusted successfully');
      setAdjustModal({ open: false, row: null });
      loadBalances();
    } catch (e) { message.error(e?.response?.data?.message || 'Adjustment failed'); }
  };

  const submitAddBalance = async () => {
    try {
      const v = await addBalanceForm.validateFields();
      setAddingBalance(true);
      await createLeaveBalance({ employeeId: selectedEmployee, leaveType: v.leaveType, allocated: v.allocated });
      message.success('Balance added successfully');
      setAddBalanceModal(false);
      addBalanceForm.resetFields();
      loadBalances();
    } catch (e) { 
      if (!e?.errorFields) message.error(e?.response?.data?.message || 'Failed to add balance');
    } finally {
      setAddingBalance(false);
    }
  };

  const handleDeleteBalance = async (id) => {
    try {
      await deleteLeaveBalance(id);
      message.success('Balance deleted successfully');
      loadBalances();
    } catch (e) { message.error(e?.response?.data?.message || 'Deletion failed'); }
  };

  const columns = [
    { title: 'Ref', dataIndex: 'reference', width: 110, render: (v) => <Text code>{v}</Text> },
    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
    { title: 'Type', dataIndex: 'leaveType', width: 110, render: (v) => tagLabel(LEAVE_TYPE_OPTIONS, v) },
    { title: 'From', dataIndex: 'fromDate', width: 110, render: (v) => dayjs(v).format('DD/MM/YYYY') },
    { title: 'To', dataIndex: 'toDate', width: 110, render: (v) => dayjs(v).format('DD/MM/YYYY') },
    { title: 'Days', dataIndex: 'days', width: 70 },
    { title: 'Status', dataIndex: 'status', width: 140, render: (v) => <Tag color={tagColor(LEAVE_STATUS_OPTIONS, v)}>{tagLabel(LEAVE_STATUS_OPTIONS, v)}</Tag> },
    {
      title: 'Actions', key: 'a', width: 200, fixed: 'right',
      render: (_, r) => (
        <Space>
          {isManagerOrHr && r.status === 'PENDING_MANAGER' && (
            <>
              <Popconfirm title="Approve & forward to HR?" onConfirm={() => act(leaveManagerAction, r._id, { approve: true })}><Button size="small" type="primary" icon={<CheckOutlined />} /></Popconfirm>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => { setReason(''); setReasonModal({ open: true, row: r, kind: 'managerReject' }); }} />
            </>
          )}
          {isManagerOrHr && r.status === 'PENDING_HR' && (
            <>
              <Popconfirm title="Approve leave?" onConfirm={() => act(leaveHrAction, r._id, { approve: true })}><Button size="small" type="primary" icon={<CheckOutlined />}>HR OK</Button></Popconfirm>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => { setReason(''); setReasonModal({ open: true, row: r, kind: 'hrReject' }); }} />
            </>
          )}
          {['PENDING_MANAGER', 'PENDING_HR'].includes(r.status) && <Popconfirm title="Cancel this request?" onConfirm={() => act(cancelLeave, r._id)}><Button size="small">Cancel</Button></Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><CarryOutOutlined style={{ marginRight: 8 }} />Leave Management</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Employee → Reporting Manager → HR</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => activeTab === 'applications' ? loadLeaves() : loadBalances()} /></Tooltip>
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'applications', label: 'Leave Applications',
          children: (
            <>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[12, 8]}>
                  <Col xs={12} sm={6}><Select allowClear style={{ width: '100%' }} placeholder="Status" options={LEAVE_STATUS_OPTIONS} value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))} /></Col>
                  <Col xs={12} sm={6}><Select allowClear style={{ width: '100%' }} placeholder="Type" options={LEAVE_TYPE_OPTIONS} value={filters.leaveType} onChange={(v) => setFilters((f) => ({ ...f, leaveType: v || null }))} /></Col>
                </Row>
              </Card>
              <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 50 }} />
            </>
          )
        },
        {
          key: 'balances', label: 'Leave Balances',
          children: (
            <>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[12, 8]} align="middle" justify="space-between">
                  <Col xs={24} sm={16}>
                    <Space style={{ width: '100%' }}>
                      <Text strong>Select Employee: </Text>
                      <Select allowClear showSearch optionFilterProp="children" style={{ width: 250 }} placeholder="Search employee..." value={selectedEmployee} onChange={setSelectedEmployee}>
                        {employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name} {e.employeeCode ? `(${e.employeeCode})` : ''}</Select.Option>)}
                      </Select>
                    </Space>
                  </Col>
                  <Col>
                    {selectedEmployee && canEditLeave && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddBalanceModal(true)}>Add Balance</Button>
                    )}
                  </Col>
                </Row>
              </Card>
              
              {selectedEmployee ? (
                <Table size="small" rowKey="_id" dataSource={balances} loading={balancesLoading} pagination={false}
                  columns={[
                    { title: 'Leave Type', dataIndex: 'leaveType', render: (v) => <Text strong>{tagLabel(LEAVE_TYPE_OPTIONS, v)}</Text> },
                    { title: 'Allocated (Total)', dataIndex: 'allocated', align: 'right' },
                    { title: 'Used', dataIndex: 'used', align: 'right' },
                    { title: 'Pending', dataIndex: 'pending', align: 'right' },
                    { title: 'Available', dataIndex: 'available', align: 'right', render: (v, r) => <Text type="success" strong>{(r.allocated || 0) - (r.used || 0) - (r.pending || 0)}</Text> },
                    { title: 'Actions', align: 'right', render: (_, r) => (
                      <Space>
                        <Button size="small" type="link" icon={<EditOutlined />} disabled={!canEditLeave} onClick={() => {
                          setNewAllocated(r.allocated || 0);
                          setAdjustModal({ open: true, row: r });
                        }}>Adjust Limit</Button>
                        <Popconfirm title="Delete this balance record?" onConfirm={() => handleDeleteBalance(r._id)}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!canEditLeave} />
                        </Popconfirm>
                      </Space>
                    )}
                  ]}
                  locale={{ emptyText: 'No balances found for this employee. Click "Add Balance" to create one.' }}
                />
              ) : (
                <Card size="small"><Text type="secondary">Please select an employee to view their leave balances.</Text></Card>
              )}
            </>
          )
        }
      ]} />

      <Modal title="Reject Leave" open={reasonModal.open} onCancel={() => setReasonModal({ open: false, row: null, kind: null })} onOk={submitReason} okButtonProps={{ danger: true }} destroyOnClose>
        <Text>Reason</Text>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginTop: 4 }} />
      </Modal>

      <Modal title="Adjust Leave Balance" open={adjustModal.open} onCancel={() => setAdjustModal({ open: false, row: null })} onOk={submitAdjustBalance} destroyOnClose>
        {adjustModal.row && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Adjusting total allocated limit for <Text strong>{tagLabel(LEAVE_TYPE_OPTIONS, adjustModal.row.leaveType)}</Text>.</Text>
            <Text type="secondary">Note: Available balance is calculated as (Allocated - Used - Pending).</Text>
            <div>
              <Text strong>New Allocated Limit: </Text>
              <InputNumber min={0} max={365} value={newAllocated} onChange={setNewAllocated} style={{ width: 120 }} />
            </div>
          </Space>
        )}
      </Modal>

      <Modal title="Add Leave Balance" open={addBalanceModal} onCancel={() => { setAddBalanceModal(false); addBalanceForm.resetFields(); }} onOk={submitAddBalance} okButtonProps={{ loading: addingBalance }} destroyOnClose>
        <Form form={addBalanceForm} layout="vertical">
          <Form.Item name="leaveType" label="Leave Type" rules={[{ required: true, message: 'Please select a leave type' }]}>
            <Select options={LEAVE_TYPE_OPTIONS} placeholder="Select Leave Type" />
          </Form.Item>
          <Form.Item name="allocated" label="Allocated Quota" initialValue={0} rules={[{ required: true }]}>
            <InputNumber min={0} max={365} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
